import React from 'react';
import { Modal, Button, Icon } from 'semantic-ui-react';
import { dispatch } from '@rematch/core';
import { toastr } from 'react-redux-toastr';

const pluralizeCredits = (amount) => `${amount.toLocaleString()} credit${amount === 1 ? '' : 's'}`;

class TopUpFreeCreditsModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            amount: 0,
            submitting: false,
        }
    }

    componentDidUpdate(prevProps) {
        if (!prevProps.topUpCreditsModal && this.props.topUpCreditsModal) {
            const { topUpCreditsProps } = this.props;
            const shortfall = Math.max(
                (topUpCreditsProps && topUpCreditsProps.requiredCredits || 0) -
                (topUpCreditsProps && topUpCreditsProps.availableCredits || 0),
                0
            );
            const freeCreditsRemaining = (topUpCreditsProps && topUpCreditsProps.freeCreditsRemaining) || 0;
            this.setState({ amount: Math.min(shortfall, freeCreditsRemaining) || 0 });
        }
    }

    close = () => {
        dispatch.app.setTopUpCreditsModal(false);
        dispatch.app.setTopUpCreditsProps(null);
    }

    gotToUpgradePage = () => {
        this.close();
        const history = this.props.history;
        history.push({
            pathname: `/app/${this.props.accountId}/settings/consumptionDetails`
        });
    }

    handleAmountChange = (e) => {
        const freeCreditsRemaining = (this.props.topUpCreditsProps && this.props.topUpCreditsProps.freeCreditsRemaining) || 0;
        let amount = parseInt(e.target.value, 10);
        if (isNaN(amount)) amount = 0;
        if (amount > freeCreditsRemaining) amount = freeCreditsRemaining;
        if (amount < 0) amount = 0;
        this.setState({ amount });
    }

    submitTopUp = async () => {
        const { accountId } = this.props;
        const { amount } = this.state;
        if (!amount || amount <= 0) {
            return;
        }
        this.setState({ submitting: true });
        try {
            await dispatch.account.topUpFreeCredits({ accountId, amount });
            this.setState({ submitting: false });
            this.close();
            toastr.success('Credits Added', `${pluralizeCredits(amount)} moved from your free reserve.`);
        } catch (err) {
            this.setState({ submitting: false });
            toastr.error('Top Up Failed', (err && err.message) || 'Could not top up credits. Please try again.');
        }
    }

    render() {
        const { topUpCreditsProps, topUpCreditsModal } = this.props;
        const { amount, submitting } = this.state;

        const availableCredits = (topUpCreditsProps && topUpCreditsProps.availableCredits) || 0;
        const requiredCredits = (topUpCreditsProps && topUpCreditsProps.requiredCredits) || 0;
        const freeCreditsRemaining = (topUpCreditsProps && topUpCreditsProps.freeCreditsRemaining) || 0;
        const shortfall = Math.max(requiredCredits - availableCredits, 0);

        return (
            <Modal
                className="premium-resource-modal"
                open={topUpCreditsModal}
                size="small"
                onClose={this.close}
                closeIcon={<Icon name="close" className="internal-close-icon" />}
            >
                <div className="modal-inner-padding">
                    <Modal.Header>
                        <div className="header-title">{shortfall > 0 ? "You're almost out of plan credits" : "Top up plan credits"}</div>
                    </Modal.Header>

                    <Modal.Content>
                        <div className="header-subtitle">
                            {shortfall > 0
                                ? `${availableCredits.toLocaleString()} credits left this billing period. This action needs ${shortfall.toLocaleString()} more credits.`
                                : `You have ${availableCredits.toLocaleString()} paid credits left this billing period.`}
                        </div>

                        <div className="topup-amount-row" style={{ margin: '20px 0' }}>
                            <label style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Top up from your free credit reserve ({freeCreditsRemaining.toLocaleString()} available):</span>
                                <strong>{amount.toLocaleString()}</strong>
                            </label>
                            <input
                                type="range"
                                min={0}
                                max={freeCreditsRemaining}
                                value={amount}
                                onChange={this.handleAmountChange}
                                style={{ width: '100%', marginTop: '10px' }}
                            />
                        </div>

                        <div className="renewal-infobox">
                            <div className='renewal-icon'>
                                <Icon name="warning sign" />
                            </div>
                            <div className="renewal-text">
                                Choose only what you need — any free credits left in reserve are forfeited when this billing period ends.
                            </div>
                        </div>
                    </Modal.Content>
                </div>
                <Modal.Actions className="modal-footer-actions">
                    <Button className="btn-cancel" size="tiny" onClick={shortfall > 0 ? this.gotToUpgradePage : this.close}>
                        {shortfall > 0 ? "Not now — upgrade instead" : "Cancel"}
                    </Button>
                    <Button
                        className="button-submit"
                        size="tiny"
                        loading={submitting}
                        disabled={submitting || !amount || amount <= 0}
                        onClick={this.submitTopUp}
                    >
                        Top up {pluralizeCredits(amount || 0)}
                    </Button>
                </Modal.Actions>
            </Modal>
        );
    }
}

export default TopUpFreeCreditsModal;
